import { useEffect, useState, useCallback, useMemo } from "react";
import { api } from "@/lib/api";
import { eur, dateIt } from "@/lib/format";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Calendar as CalendarIcon, Search, RotateCcw } from "lucide-react";

const emptyF = { q: "", course_id: "all", modality: "all", date_from: "", date_to: "" };
const modLabel = (m) => (m === "videoconferenza" ? "Videoconferenza" : m === "aula" ? "Aula" : m || "—");

function EditionFilters({ f, setF, courses, count }) {
  const set = (k, v) => setF({ ...f, [k]: v });
  return (
    <Card className="border-slate-200"><CardContent className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3 items-end">
      <div className="lg:col-span-2">
        <Label className="text-xs">Cerca</Label>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={f.q} onChange={(e) => set("q", e.target.value)} placeholder="Corso, sede, docente…" className="pl-9" data-testid="editions-search-input" />
        </div>
      </div>
      <div><Label className="text-xs">Corso</Label>
        <Select value={f.course_id} onValueChange={(v) => set("course_id", v)}>
          <SelectTrigger data-testid="editions-course-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Tutti i corsi</SelectItem>{courses.map((c) => <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div><Label className="text-xs">Modalità</Label>
        <Select value={f.modality} onValueChange={(v) => set("modality", v)}>
          <SelectTrigger data-testid="editions-modality-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Tutte</SelectItem><SelectItem value="aula">Aula</SelectItem><SelectItem value="videoconferenza">Videoconferenza</SelectItem></SelectContent>
        </Select>
      </div>
      <div><Label className="text-xs">Dal</Label><Input type="date" value={f.date_from} onChange={(e) => set("date_from", e.target.value)} data-testid="editions-date-from" /></div>
      <div><Label className="text-xs">Al</Label><Input type="date" value={f.date_to} onChange={(e) => set("date_to", e.target.value)} data-testid="editions-date-to" /></div>
      <div className="lg:col-span-6 flex items-center justify-between">
        <span className="text-sm text-slate-500" data-testid="editions-count">{count} edizioni</span>
        <Button variant="outline" size="sm" onClick={() => setF(emptyF)} data-testid="editions-filter-reset"><RotateCcw className="w-4 h-4 mr-1" /> Azzera filtri</Button>
      </div>
    </CardContent></Card>
  );
}

const empty = { course_id: "", start_date: "", end_date: "", start_time: "09:00", location: "", modality: "aula", capacity: 20, teacher: "", price: 0, calendar_url: "", published: true };

export default function AdminEditions() {
  const [editions, setEditions] = useState([]);
  const [courses, setCourses] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [f, setF] = useState(emptyF);

  const load = useCallback(() => api.get("/editions").then(r => setEditions(r.data)), []);
  useEffect(() => { load(); api.get("/courses").then(r => setCourses(r.data)); }, [load]);
  const editionCourses = useMemo(() => courses.filter((c) => editions.some((e) => e.course_id === c.id)), [courses, editions]);
  const filtered = useMemo(() => {
    const s = f.q.trim().toLowerCase();
    return editions.filter((e) => (f.course_id === "all" || e.course_id === f.course_id)
      && (f.modality === "all" || e.modality === f.modality)
      && (!f.date_from || (e.end_date || e.start_date) >= f.date_from)
      && (!f.date_to || e.start_date <= f.date_to)
      && (!s || [e.course_title, e.location, e.teacher].some((v) => (v || "").toLowerCase().includes(s))));
  }, [editions, f]);

  const save = async () => {
    try {
      if (editing) await api.put(`/editions/${editing}`, form);
      else await api.post("/editions", form);
      toast.success(editing ? "Edizione aggiornata" : "Edizione creata");
      setOpen(false); setEditing(null); setForm(empty); load();
    } catch { toast.error("Errore salvataggio"); }
  };
  const del = async (id) => { if (!window.confirm("Eliminare l'edizione?")) return; await api.delete(`/editions/${id}`); toast.success("Edizione eliminata"); load(); };
  const edit = (e) => { setEditing(e.id); setForm({ ...empty, ...e }); setOpen(true); };

  return (
    <div className="space-y-6" data-testid="admin-editions">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold text-slate-900">Calendario edizioni</h1>
          <p className="text-slate-500 mt-1">Programma nuove edizioni per corsi in aula e videoconferenza.</p>
        </div>
        <Button onClick={() => { setEditing(null); setForm(empty); setOpen(true); }} className="bg-[#0F2942] hover:bg-[#1B3B5F]" data-testid="new-edition-button">
          <Plus className="w-4 h-4 mr-1" /> Nuova edizione
        </Button>
      </div>
      <EditionFilters f={f} setF={setF} courses={editionCourses} count={filtered.length} />
      <Card className="border-slate-200"><CardContent className="p-0">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Corso</TableHead><TableHead>Modalità</TableHead><TableHead>Data</TableHead><TableHead>Orario</TableHead>
            <TableHead>Sede</TableHead><TableHead>Docente</TableHead>
            <TableHead>Calendario</TableHead>
            <TableHead>Prezzo</TableHead><TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {filtered.map((e) => (
              <TableRow key={e.id} data-testid={`edition-row-${e.id}`}>
                <TableCell className="font-medium">{e.course_title}</TableCell>
                <TableCell className="text-xs">{modLabel(e.modality)}</TableCell>
                <TableCell>{dateIt(e.start_date)}</TableCell>
                <TableCell>{e.start_time}</TableCell>
                <TableCell>{e.location}</TableCell>
                <TableCell>{e.teacher}</TableCell>
                <TableCell>
                  {e.calendar_url ? (
                    <a href={e.calendar_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sky-700 hover:underline text-xs" data-testid={`edition-calendar-link-${e.id}`}>
                      <CalendarIcon className="w-3.5 h-3.5" /> Apri PDF
                    </a>
                  ) : <span className="text-slate-400 text-xs">—</span>}
                </TableCell>
                <TableCell>{eur(e.price)}</TableCell>
                <TableCell>
                  <Button variant="ghost" size="icon" onClick={() => edit(e)}><Pencil className="w-4 h-4" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => del(e.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                </TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && <TableRow><TableCell colSpan={9} className="text-center text-slate-500 py-10" data-testid="editions-empty">Nessuna edizione trovata.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </CardContent></Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editing ? "Modifica edizione" : "Nuova edizione"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Corso</Label>
              <Select value={form.course_id} onValueChange={(v) => { const c = courses.find((x) => x.id === v); setForm({ ...form, course_id: v, modality: c?.modality === "videoconferenza" ? "videoconferenza" : "aula" }); }}>
                <SelectTrigger data-testid="edition-course-select"><SelectValue placeholder="Seleziona corso" /></SelectTrigger>
                <SelectContent>{courses.filter((c) => ["aula", "videoconferenza"].includes(c.modality) || c.id === form.course_id).map((c) => <SelectItem key={c.id} value={c.id}>{c.title} · {c.modality === "aula" ? "Aula" : c.modality === "videoconferenza" ? "Videoconferenza" : "E-learning"}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Inizio</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="edition-start-date" /></div>
            <div><Label>Fine</Label><Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></div>
            <div><Label>Orario</Label><Input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} /></div>
            <div><Label>Modalità</Label>
              <Select value={form.modality} onValueChange={(v) => setForm({ ...form, modality: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="aula">Aula</SelectItem><SelectItem value="videoconferenza">Videoconferenza</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label>Sede</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} /></div>
            <div><Label>Docente</Label><Input value={form.teacher} onChange={(e) => setForm({ ...form, teacher: e.target.value })} /></div>
            <div><Label>Capienza</Label><Input type="number" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: parseInt(e.target.value || "0") })} /></div>
            <div className="col-span-2"><Label>Calendario (URL PDF)</Label>
              <Input type="url" placeholder="https://dominio.it/calendario-edizione.pdf"
                value={form.calendar_url || ""}
                onChange={(e) => setForm({ ...form, calendar_url: e.target.value })}
                data-testid="edition-input-calendar-url" />
              <div className="text-xs text-slate-500 mt-1">Facoltativo. Se valorizzato, i partner vedranno un link "Visualizza Calendario" sull'edizione.</div>
            </div>
            <div><Label>Prezzo €</Label><Input type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: parseFloat(e.target.value || "0") })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={save} className="bg-[#0F2942] hover:bg-[#1B3B5F]" data-testid="save-edition">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
